import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { legalLinks, ROUTES } from '../data/siteLinks';
import {
  ANALYTICS_CONSENT_KEY,
  CONSENT_SETTINGS_EVENT,
  CONSENT_VERSION_KEY,
  CURRENT_CONSENT_VERSION,
  setAnalyticsConsent,
  trackPageView,
} from '../utils/analytics';

const CookieConsent: React.FC = () => {
  const [bannerVisible, setBannerVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    const storedAnalytics = localStorage.getItem(ANALYTICS_CONSENT_KEY);
    const consentVersion = localStorage.getItem(CONSENT_VERSION_KEY);

    if (storedAnalytics) {
      setAnalyticsEnabled(storedAnalytics === 'true');
    }

    if (!consent || consentVersion !== CURRENT_CONSENT_VERSION) {
      const timer = window.setTimeout(() => setBannerVisible(true), 500);
      return () => window.clearTimeout(timer);
    }

    return undefined;
  }, []);

  useEffect(() => {
    const openSettings = () => {
      setAnalyticsEnabled(localStorage.getItem(ANALYTICS_CONSENT_KEY) === 'true');
      setBannerVisible(true);
      setSettingsOpen(true);
      setClosing(false);
    };

    window.addEventListener(CONSENT_SETTINGS_EVENT, openSettings);
    return () => window.removeEventListener(CONSENT_SETTINGS_EVENT, openSettings);
  }, []);

  const closeBanner = () => {
    setBannerVisible(false);
    setSettingsOpen(false);
    setClosing(false);
  };

  const handleClose = (choice: 'accepted' | 'declined' | 'dismissed') => {
    setClosing(true);
    window.setTimeout(() => {
      const enabled = choice === 'accepted';
      localStorage.setItem('cookie_consent', choice);
      setAnalyticsConsent(enabled);
      localStorage.removeItem('cookie_marketing');
      if (enabled) trackPageView();
      closeBanner();
    }, 300);
  };

  const handleSaveSettings = () => {
    localStorage.setItem('cookie_consent', 'custom');
    setAnalyticsConsent(analyticsEnabled);
    localStorage.removeItem('cookie_marketing');
    if (analyticsEnabled) trackPageView();
    setSettingsOpen(false);
    setClosing(true);
    window.setTimeout(() => {
      setBannerVisible(false);
      setClosing(false);
    }, 300);
  };

  if (!bannerVisible) return null;

  return (
    <>
      {!settingsOpen && (
        <div
          className={`consent-banner-root${closing ? ' closing' : ''}`}
          role="region"
          aria-labelledby="cookies_policy_description"
          tabIndex={-1}
          style={s.banner}
        >
          <div style={s.bannerInner}>
            <div style={s.bannerContent}>
              <div style={s.bannerText}>
                <span id="cookies_policy_description" style={s.description}>
                  Zorunlu depolama, çerez tercihlerinizi hatırlamak için kullanılır. Onay verirseniz Google Analytics ile site kullanımını ve önemli bağlantılara yapılan tıklamaları ölçeriz. Analiz çerezlerini reddetmeniz sitenin kullanımını etkilemez. Tercihinizi dilediğiniz zaman değiştirebilirsiniz. Ayrıntılar için{' '}
                  <Link to={ROUTES.privacy} style={s.policyLink}>Gizlilik Kuralları</Link>
                  {' '}sayfasını inceleyebilirsiniz.
                </span>
                <div style={s.legalLinksRow}>
                  {legalLinks.map((item) => (
                    <Link key={item.path} to={item.path} style={s.legalLink}>
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
              <div style={s.bannerButtons}>
                <span
                  role="button"
                  tabIndex={0}
                  style={s.settingsBtn}
                  onClick={() => setSettingsOpen(true)}
                  onKeyDown={(e) => e.key === 'Enter' && setSettingsOpen(true)}
                >
                  Ayarlar
                </span>
                <button style={s.declineBtn} onClick={() => handleClose('declined')}>
                  Tümünü Reddet
                </button>
                <button style={s.acceptBtn} onClick={() => handleClose('accepted')}>
                  Kabul Et
                </button>
              </div>
            </div>
            <span style={s.closeBtnWrap}>
              <button style={s.closeBtn} onClick={() => handleClose('dismissed')} aria-label="Kapat">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M17.294 6l.706.706L12.705 12 18 17.294l-.706.706L12 12.705 6.706 18 6 17.294 11.295 12 6 6.706 6.706 6 12 11.295 17.294 6z"/>
                </svg>
              </button>
            </span>
          </div>
        </div>
      )}

      {settingsOpen && (
        <div style={s.overlay} onClick={() => setSettingsOpen(false)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>Gelişmiş Çerez Ayarları</h2>
              <button style={s.modalCloseBtn} onClick={() => setSettingsOpen(false)} aria-label="Kapat">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M17.294 6l.706.706L12.705 12 18 17.294l-.706.706L12 12.705 6.706 18 6 17.294 11.295 12 6 6.706 6.706 6 12 11.295 17.294 6z"/>
                </svg>
              </button>
            </div>

            <div style={s.category}>
              <div style={s.categoryHeader}>
                <span style={s.categoryTitle}>Temel Çerezler</span>
                <div style={{ ...s.toggleTrack, ...s.toggleTrackOn, opacity: 0.6, cursor: 'not-allowed' }}>
                  <div style={{ ...s.toggleThumb, ...s.toggleThumbOn }} />
                </div>
              </div>
              <p style={s.categoryDesc}>
                Bu çerezler güvenlik, kimlik doğrulaması ve ağ yönetimi gibi temel işlevleri etkinleştirirler ve devre dışı bırakılamazlar.
              </p>
            </div>

            <div style={s.divider} />

            <div style={s.category}>
              <div style={s.categoryHeader}>
                <span style={s.categoryTitle}>Analiz Çerezlerini Etkinleştirin</span>
                <div
                  style={analyticsEnabled ? { ...s.toggleTrack, ...s.toggleTrackOn } : s.toggleTrack}
                  onClick={() => setAnalyticsEnabled(!analyticsEnabled)}
                  role="switch"
                  aria-checked={analyticsEnabled}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setAnalyticsEnabled(!analyticsEnabled)}
                >
                  <div style={analyticsEnabled ? { ...s.toggleThumb, ...s.toggleThumbOn } : s.toggleThumb} />
                </div>
              </div>
              <p style={s.categoryDesc}>
                Bu çerezler yalnızca onayınızla Google Analytics'i çalıştırır. Ziyaret edilen sayfalar ile hizmet, WhatsApp ve randevu bağlantılarına yapılan tıklamaları ölçerek siteyi geliştirmemize yardımcı olur. Reklam kişiselleştirmesi kullanılmaz.
              </p>
            </div>

            <div style={s.modalLegalLinks}>
              {legalLinks.map((item) => (
                <Link key={item.path} to={item.path} style={s.modalLegalLink}>
                  {item.label}
                </Link>
              ))}
            </div>

            <div style={s.modalFooter}>
              <button style={s.saveBtn} onClick={handleSaveSettings}>
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const s: Record<string, React.CSSProperties> = {
  banner: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 10000,
    backgroundColor: '#fff',
    boxShadow: '0 -2px 16px rgba(0,0,0,0.15)',
    animation: 'slideUp 0.3s ease-out',
  },
  bannerInner: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '16px 24px',
    gap: '16px',
  },
  bannerContent: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    gap: '24px',
    flexWrap: 'wrap' as const,
  },
  bannerText: {
    flex: '1 1 400px',
  },
  description: {
    fontSize: '13px',
    lineHeight: '1.6',
    color: 'rgb(36, 35, 35)',
  },
  policyLink: {
    color: 'rgb(16, 190, 132)',
    textDecoration: 'underline',
    fontWeight: 500,
  },
  legalLinksRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '12px',
    marginTop: '10px',
  },
  legalLink: {
    color: 'rgb(91, 90, 90)',
    fontSize: '12px',
    textDecoration: 'underline',
  },
  bannerButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0,
    flexWrap: 'wrap' as const,
  },
  settingsBtn: {
    fontSize: '13px',
    fontWeight: 500,
    color: 'rgb(36, 35, 35)',
    cursor: 'pointer',
    padding: '8px 16px',
    border: '1px solid rgb(200, 200, 200)',
    borderRadius: '0',
    backgroundColor: 'transparent',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap' as const,
  },
  declineBtn: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#fff',
    backgroundColor: 'rgb(36, 35, 35)',
    border: 'none',
    borderRadius: '0',
    padding: '8px 20px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap' as const,
  },
  acceptBtn: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#fff',
    backgroundColor: 'rgb(16, 190, 132)',
    border: 'none',
    borderRadius: '0',
    padding: '8px 20px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap' as const,
  },
  closeBtnWrap: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    color: 'rgb(91, 90, 90)',
    display: 'flex',
    alignItems: 'center',
    transition: 'color 0.2s',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 10001,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: 'fadeIn 0.2s ease-out',
  },
  modal: {
    backgroundColor: '#fff',
    width: '600px',
    maxWidth: '90vw',
    maxHeight: '80vh',
    overflowY: 'auto' as const,
    boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
    borderRadius: '0',
    padding: '32px',
    position: 'relative' as const,
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '28px',
  },
  modalTitle: {
    fontSize: '22px',
    fontWeight: 700,
    color: 'rgb(36, 35, 35)',
    margin: 0,
  },
  modalCloseBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    color: 'rgb(36, 35, 35)',
    display: 'flex',
    alignItems: 'center',
  },
  category: {
    padding: '16px 0',
  },
  categoryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  categoryTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: 'rgb(36, 35, 35)',
  },
  categoryDesc: {
    fontSize: '13px',
    lineHeight: '1.6',
    color: 'rgb(91, 90, 90)',
    margin: 0,
  },
  divider: {
    height: '1px',
    backgroundColor: 'rgb(200, 200, 200)',
  },
  toggleTrack: {
    width: '44px',
    height: '24px',
    borderRadius: '12px',
    backgroundColor: 'rgb(200, 200, 200)',
    position: 'relative' as const,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    flexShrink: 0,
  },
  toggleTrackOn: {
    backgroundColor: 'rgb(16, 190, 132)',
  },
  toggleThumb: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: '#fff',
    position: 'absolute' as const,
    top: '2px',
    left: '2px',
    transition: 'left 0.2s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  },
  toggleThumbOn: {
    left: '22px',
  },
  modalLegalLinks: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '12px',
    marginTop: '20px',
  },
  modalLegalLink: {
    color: 'rgb(36, 35, 35)',
    fontSize: '13px',
    textDecoration: 'underline',
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '24px',
  },
  saveBtn: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#fff',
    backgroundColor: 'rgb(36, 35, 35)',
    border: 'none',
    borderRadius: '0',
    padding: '12px 32px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
};

export default CookieConsent;
